// E37 PR10 — THE INBOX.   route: /inbox
//
// Contract: e35-37-design.md §5 PR10 — "Inbox.tsx + nav unread badge; click =
//   optimistic read + navigate". The list is ONE door's answer (GET
//   /api/inbox): what mentioned you, when, in which org and KB — and each
//   entry opens the PUBLISHED dashboard at the owner's #/r/ or #/c/ hash,
//   which is a full document navigation on purpose: the target is the
//   Worker-served artifact, not an app route, and framing it here would put a
//   second copy of DashboardView's reasoning around a page that owns its own
//   viewport.
// Contract: e35-37-design.md L20 — an org without a stored membership handle
//   joined nothing, and this page SAYS so ("no handle set for this org")
//   rather than showing an empty list that reads as "nobody needs you".
// Contract: the per-panel degrade doctrine (Reports.tsx, Billing.tsx): a KB
//   whose fan-out leg failed costs its own rows and a visible sentence, never
//   the screen — the envelope's per-KB states are what make that honest.
//
// READ STATE IS OPTIMISTIC AND OWNED HERE: opening an item marks it read
// locally and fires the POST without awaiting it (the verb is idempotent, so
// a lost answer costs a refetch nothing); the toggle awaits, because its
// whole job is the mark. Every change hands the new unread count up through
// `onUnread`, which is how the shell's badge stays the page's number.
import { useEffect, useState } from 'react';

import { Badge } from '@astryxdesign/core/Badge';
import { Button } from '@astryxdesign/core/Button';
import { Heading } from '@astryxdesign/core/Heading';
import { Spinner } from '@astryxdesign/core/Spinner';
import { Stack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';

import type { InboxClient, InboxItem, InboxOrgReport, MentionTier } from '../api/inbox';
import { Chip, type ChipState } from '../components/Chip';
import { messageOf } from '../format/errors';
import { relShort } from '../format/units';

import './inbox.css';

/** The tier → chip vocabulary. Canonical is committed content (built); the
 *  live tiers are the working layer's — current, not yet in a commit. */
export const TIER_CHIP: Record<MentionTier, ChipState> = {
  canonical: 'built',
  operational: 'partial',
  conversational: 'unspecified',
};

export interface InboxPageProps {
  client: InboxClient;
  /** the new unread count, every time this page learns one — the shell's badge */
  onUnread?: (unread: number) => void;
  /** where an entry opens. Default: a full document navigation to the
   *  published dashboard (`window.location.assign`) — injectable for tests. */
  onOpen?: (href: string) => void;
}

export function InboxPage({ client, onUnread, onOpen }: InboxPageProps) {
  const [items, setItems] = useState<InboxItem[] | null>(null);
  const [orgs, setOrgs] = useState<InboxOrgReport[]>([]);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const open = onOpen ?? ((href: string) => window.location.assign(href));

  useEffect(() => {
    let live = true;
    void client
      .list()
      .then((answer) => {
        if (!live) return;
        setItems(answer.items);
        setOrgs(answer.orgs);
        onUnread?.(answer.unread);
      })
      .catch((error) => {
        if (live) setRefusal(messageOf(error));
      });
    return () => {
      live = false;
    };
    // one read per mount: the page's own marks keep the copy current after it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  /** apply a read-state change locally and hand the new count up */
  const applyRead = (keys: Set<string>, read: boolean) => {
    setItems((current) => {
      if (current === null) return current;
      const next = current.map((item) =>
        keys.has(`${item.org}\n${item.kb}\n${item.key}`) ? { ...item, read } : item,
      );
      onUnread?.(next.filter((item) => !item.read).length);
      return next;
    });
  };

  const keyOf = (item: InboxItem) => `${item.org}\n${item.kb}\n${item.key}`;
  const refOf = (item: InboxItem) => ({ org: item.org, kb: item.kb, key: item.key });

  // E39 PR10 — THE TWO KINDS, told apart by the envelope's own word and by
  // nothing else. A mention renders the sentence that named you; a watch item
  // renders what happened and who did it, because "Doing → Done" without a
  // person is a fact nobody can act on. Everything else about the row — the
  // tier chip, the coordinates, the read dot, the open button — is shared,
  // deliberately: it is ONE list, with two reasons for being in it.
  const lineOf = (item: InboxItem) =>
    item.kind === 'watch'
      ? `${item.actor ? `${item.actor} ` : ''}${item.event}${item.context ? `: ${item.context}` : ''}`
      : item.context || item.raw;
  const labelOf = (item: InboxItem) =>
    item.kind === 'watch'
      ? `Open the followed record in ${item.org}/${item.kb}`
      : `Open the mention in ${item.org}/${item.kb}`;

  const openItem = (item: InboxItem) => {
    // optimistic read + navigate (§5 PR10). The POST is not awaited: the mark
    // is idempotent and the navigation is the reader's intent — a slow write
    // must not hold the door.
    if (!item.read) {
      applyRead(new Set([keyOf(item)]), true);
      void client.markRead([refOf(item)]).catch(() => {
        /* the next list read answers the truth */
      });
    }
    if (item.href) open(item.href);
  };

  const toggleRead = async (item: InboxItem) => {
    try {
      if (item.read) {
        await client.markUnread([refOf(item)]);
        applyRead(new Set([keyOf(item)]), false);
      } else {
        await client.markRead([refOf(item)]);
        applyRead(new Set([keyOf(item)]), true);
      }
    } catch (error) {
      setRefusal(messageOf(error));
    }
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      await client.markAllReadBefore(new Date().toISOString());
      if (items !== null) applyRead(new Set(items.map(keyOf)), true);
    } catch (error) {
      setRefusal(messageOf(error));
    } finally {
      setBusy(false);
    }
  };

  if (refusal !== null) {
    return (
      <Stack direction="vertical" gap={3} data-page="inbox">
        <Heading level={1}>Inbox</Heading>
        <Text type="body" color="secondary">
          {refusal}
        </Text>
      </Stack>
    );
  }
  if (items === null) {
    return (
      <Stack direction="vertical" gap={3} data-page="inbox">
        <Heading level={1}>Inbox</Heading>
        <Spinner aria-label="Reading your inbox" />
      </Stack>
    );
  }

  const unread = items.filter((item) => !item.read).length;
  // the scope's honesty rows (L20 + per-KB degrade): said in words, above the
  // list, so an empty or thinner-than-expected inbox explains itself
  const unjoined = orgs.filter((o) => o.handle === null);
  const failed = orgs.flatMap((o) =>
    o.kbs.filter((k) => k.state === 'failed').map((k) => `${o.org}/${k.kb}`),
  );

  return (
    <Stack direction="vertical" gap={4} data-page="inbox">
      <Stack direction="horizontal" gap={3} vAlign="center">
        <Heading level={1}>Inbox</Heading>
        {unread > 0 ? <Badge label={`${unread} unread`} variant="info" /> : null}
        <span className="dj-inbox-spacer" />
        {unread > 0 ? (
          <Button
            variant="secondary"
            size="sm"
            label="Mark all read"
            isDisabled={busy}
            onClick={() => void markAllRead()}
          />
        ) : null}
      </Stack>
      <Text type="body" color="secondary">
        Where you were mentioned, and what happened to what you follow — across every knowledge
        base you can reach.
      </Text>

      {unjoined.map((o) => (
        <Text key={o.org} type="supporting" color="secondary" data-inbox-nohandle={o.org}>
          No handle is set for {o.org}, so mentions there cannot reach you. An admin of that
          organization can set one.
        </Text>
      ))}
      {failed.map((kb) => (
        <Text key={kb} type="supporting" color="secondary" data-inbox-failed={kb}>
          {kb} could not be read — some of its items are missing from this list.
        </Text>
      ))}

      {items.length === 0 ? (
        <Text type="body" color="secondary">
          Nothing has mentioned you yet.
        </Text>
      ) : (
        <ul className="dj-inbox-list">
          {items.map((item) => (
            <li
              key={keyOf(item)}
              className={`dj-inbox-item${item.read ? '' : ' dj-inbox-item-unread'}`}
              data-inbox-item={item.key}
              data-inbox-kind={item.kind}
              data-read={item.read ? 'true' : 'false'}
            >
              <button
                type="button"
                className="dj-inbox-open"
                onClick={() => openItem(item)}
                aria-label={labelOf(item)}
              >
                <Stack direction="vertical" gap={1}>
                  <Stack direction="horizontal" gap={2} vAlign="center">
                    {!item.read ? <span className="dj-inbox-dot" aria-label="unread" /> : null}
                    <Chip state={TIER_CHIP[item.tier] ?? 'unspecified'}>{item.tier}</Chip>
                    <Text type="supporting" color="secondary">
                      {item.org}/{item.kb} · {item.node}
                      {item.firstAt ? ` · ${relShort(item.firstAt)}` : ''}
                    </Text>
                  </Stack>
                  <Text type="body">{lineOf(item)}</Text>
                </Stack>
              </button>
              <Button
                variant="ghost"
                size="sm"
                label={item.read ? 'Mark unread' : 'Mark read'}
                onClick={() => void toggleRead(item)}
              />
            </li>
          ))}
        </ul>
      )}
    </Stack>
  );
}

export default InboxPage;
